import React, { useEffect, useState } from "react";
import styles from "./DoorDetailPage.module.css";
import { useParams, useLocation } from "react-router-dom";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import { orderedProductImages } from "../../Services/productImages";
import { Helmet } from "react-helmet";
import ChooseVariantModal from "./ChooseVariantModal";
import { DoorDetail, DoorProductDetail, DoorRawMaterialDetail } from "../../Models/Models";
import { canAddToCart, stockStatusLabel } from "../../Services/stockAvailability";

type ItemType = "PRODUCT" | "RAW_MATERIAL";
interface LocationState {
  item?: DoorDetail;
  customer_id?: number;
}

const toDoorDetail = (match: any, type: ItemType): DoorDetail => {
  const images = orderedProductImages(match);
  const description =
    match?.design_description || match?.description || match?.product_description || "";

  if (type === "RAW_MATERIAL") {
    return {
      id: Number(match.id ?? match.product_id),
      name: match.name || match.product_name || "",
      sku_id: match.sku_id || "",
      type: "RAW_MATERIAL",
      description,
      images,
      category: match.category,
      measuring_unit: match.measuring_unit,
      measuring_unit_value: match.measuring_unit_value,
      selling_price: match.selling_price ?? match.retailer_selling_price,
    } as DoorRawMaterialDetail;
  }

  return {
    ...match,
    id: Number(match.id ?? match.product_id),
    name: match.name || match.product_name || "",
    sku_id: match.sku_id || "",
    type: "PRODUCT",
    description,
    images,
    rating: typeof match.rating === "number" ? match.rating : 0,
    review_count: typeof match.review_count === "number" ? match.review_count : 0,
    variants: match.variants ?? undefined,
  } as DoorProductDetail;
};

const DoorDetailPage: React.FC = () => {
  const { type, id, itemId } = useParams<{ type: string; id: string; itemId: string }>();
  const itemType = (type || "PRODUCT").toUpperCase() as ItemType;

  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const stateItem = state?.item;
  const customerId = state?.customer_id;

  const [item, setItem] = useState<DoorDetail | null>(
    stateItem && Number(stateItem.id) === Number(itemId)
      ? toDoorDetail(stateItem, itemType)
      : null
  );
  const [mainImgIdx, setMainImgIdx] = useState(0);
  const [loading, setLoading] = useState(
    !stateItem || Number(stateItem.id) !== Number(itemId)
  );
  const [error, setError] = useState("");
  const [showVariantModal, setShowVariantModal] = useState(false);

  useEffect(() => {
    if (!id || !itemId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        let match: any = null;

        // 1) Direct product detail (reliable for customer shop)
        try {
          const detailRes: any = await WebService.getAPI({
            action: `/v1/door-management/dashboard/type/${itemType}/products/${itemId}`,
          });
          match = detailRes?.data ?? detailRes;
          if (match && !(match.id || match.product_id)) match = null;
        } catch {
          match = null;
        }

        // 2) Watchlist for this category
        if (!match) {
          const watchRes: any = await WebService.getAPI({
            action: `/v1/door-management/dashboard/type/${itemType}/subcategories/${id}/watchlist?page_number=1&limit=100`,
          });
          const payload = watchRes?.data ?? watchRes;
          const list =
            itemType === "RAW_MATERIAL"
              ? payload?.raw_materials ?? payload?.products ?? []
              : payload?.designs ?? payload?.products ?? [];

          match = Array.isArray(list)
            ? list.find(
                (itm: any) =>
                  Number(itm.id ?? itm.product_id) === Number(itemId)
              )
            : null;
        }

        // 3) Fallback: inventory product list
        if (!match && itemType === "PRODUCT") {
          const allRes: any = await WebService.getAPI({
            action: `/v1/door-management/product?page_number=1&limit=200&status=ACTIVE`,
          });
          const products = allRes?.data?.products ?? [];
          match = Array.isArray(products)
            ? products.find((p: any) => Number(p.id) === Number(itemId))
            : null;
        }

        if (cancelled) return;
        if (match) {
          setItem(toDoorDetail(match, itemType));
          setMainImgIdx(0);
        } else if (stateItem && Number(stateItem.id) === Number(itemId)) {
          setItem(toDoorDetail(stateItem, itemType));
          setMainImgIdx(0);
        } else {
          setItem(null);
          setError("Item not found.");
        }
      } catch {
        if (!cancelled) {
          if (stateItem && Number(stateItem.id) === Number(itemId)) {
            setItem(toDoorDetail(stateItem, itemType));
            setError("");
          } else {
            setItem(null);
            setError("Could not fetch details.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemType, id, itemId]);

  const isProduct = (itm: DoorDetail): itm is DoorProductDetail =>
    itm.type === "PRODUCT";
  const isRawMaterial = (itm: DoorDetail): itm is DoorRawMaterialDetail =>
    itm.type === "RAW_MATERIAL";
  const hasImages = !!item && Array.isArray(item.images) && item.images.length > 0;

  const mainImgSrc =
    hasImages && item!.images[mainImgIdx]?.file_name
      ? mediaUrl(item!.images[mainImgIdx].file_name)
      : "/Assets/no-pictures.png";

  if (loading) {
    return <div className={styles.loader}>Loading...</div>;
  }
  if (error) {
    return <div className={styles.error}>{error}</div>;
  }
  if (!id || !type) {
    return <div className={styles.error}>Invalid URL params.</div>;
  }
  if (!item) {
    return <div className={styles.error}>No details found for this item.</div>;
  }

  const descriptionText =
    typeof item.description === "string" ? item.description.trim() : "";

  return (
    <div className={styles.detailPage}>
      <Helmet>
        <title>{item.name || "Product"} | Kunal Door and More</title>
        <meta
          name="description"
          content={descriptionText.slice(0, 160) || "Product details"}
        />
      </Helmet>

      <div className={styles.heroRow}>
        <div className={styles.imgColumn}>
          <div className={styles.mainImgWrapper}>
            {isProduct(item) && item.is_most_selling && (
              <span className={styles.mostSellingRibbon}>Trending</span>
            )}
            <img
              src={mainImgSrc}
              alt={item.name}
              className={styles.mainImg}
              loading="eager"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
              }}
            />
            {hasImages && item.images.length === 1 && (
              <div className={styles.imgBadge}>No more images</div>
            )}
            {!hasImages && (
              <div className={styles.imgBadge}>No image available</div>
            )}
          </div>
          {hasImages && item.images.length > 1 && (
            <div className={styles.thumbRow} role="list">
              {item.images.map((img, idx) => (
                <img
                  key={idx}
                  src={mediaUrl(img.file_name)}
                  alt={`${item.name} thumbnail ${idx + 1}`}
                  className={`${styles.thumbImg} ${
                    mainImgIdx === idx ? styles.activeThumb : ""
                  }`}
                  onClick={() => setMainImgIdx(idx)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Show image ${idx + 1}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setMainImgIdx(idx);
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/Assets/no-pictures.png";
                  }}
                  style={{
                    outline: mainImgIdx === idx ? "2px solid #FFD400" : "none",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.detailsColumn}>
          <h2 className={styles.doorTitle}>{item.name}</h2>
          <div className={styles.skuRow}>
            <span>SKU: {item.sku_id || "—"}</span>
            {isRawMaterial(item) && item.category && (
              <span style={{ marginLeft: 18 }}>Category: {item.category}</span>
            )}
          </div>

          {isRawMaterial(item) && item.selling_price != null && (
            <div className={styles.sellingPrice}>
              ₹{item.selling_price}
              {item.measuring_unit ? (
                <span className={styles.priceUnit}> / {item.measuring_unit}</span>
              ) : null}
            </div>
          )}

          <button
            className={`${styles.chooseVariantBtn} ${
              isProduct(item) && !canAddToCart(item) ? styles.chooseVariantBtnDisabled : ""
            }`}
            type="button"
            onClick={() => {
              if (isProduct(item) && !canAddToCart(item)) return;
              setShowVariantModal(true);
            }}
            disabled={isProduct(item) && !canAddToCart(item)}
            aria-haspopup="dialog"
            aria-controls="variant-modal"
          >
            {isProduct(item) && !canAddToCart(item)
              ? stockStatusLabel(item) || "Unavailable"
              : "Choose Variant"}
          </button>
          {isProduct(item) && !canAddToCart(item) && stockStatusLabel(item) && (
            <div className={styles.stockHint}>This design cannot be added to the cart.</div>
          )}

          <div className={styles.descBox}>
            <h3>Description</h3>
            <p className={styles.description}>
              {descriptionText ? (
                descriptionText
              ) : (
                <span style={{ color: "#bbb" }}>
                  No description for this product.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {showVariantModal && (
        <ChooseVariantModal
          onClose={() => setShowVariantModal(false)}
          door={{
            ...item,
            product_name: item.name,
            product_description:
              "design_description" in item && item.design_description
                ? String(item.design_description)
                : descriptionText,
          }}
          customerId={customerId}
          itemType={itemType}
        />
      )}
    </div>
  );
};

export default DoorDetailPage;
